import { CircularReferenceDetector } from "@/excel/circularReferenceDetector";
import { tableConverter } from "@/excel/tableConverter";
import type { ExcelFunction } from "@/services/custom-functions/customFunction.types";

const circularReferenceDetector = new CircularReferenceDetector();

export async function setFormAsTable(formItem: any, func: ExcelFunction): Promise<void> {
  await Excel.run(async (context: any) => {
    // Get the currently selected cell
    const currentCell = context.workbook.getSelectedRange();

    // Process the data into a table format
    const table = tableConverter(formItem);

    // Load the function cell (next to the table)
    const functionCell = currentCell.getOffsetRange(0, table[0]?.length + 1 || 3);

    // Get surrounding region to clear existing content
    try {
      const inputRange = currentCell.getSurroundingRegion();
      const outputRange = functionCell.getSurroundingRegion();

      inputRange.load("address");
      outputRange.load("address");
      await context.sync();

      inputRange.clear();
      outputRange.clear();

      console.log(`Clear Input Contents: ${inputRange.address}`);
      console.log(`Clear Output Contents: ${outputRange.address}`);
    } catch (error) {
      console.warn("Error clearing contents:", error);
    }

    // Write the table
    if (table.length > 0) {
      const fullRange = currentCell.getResizedRange(table.length - 1, table[0].length - 1);
      fullRange.values = table;
    }
    // Get the cell references for parameters
    const paramRanges = func.parameters.map((_, index) => currentCell.getOffsetRange(index, 1));
    paramRanges.forEach((range) => range.load("address"));
    await context.sync();
    const paramCellReferences = paramRanges.map((range) => range.address.split("!")[1]);
    // Automatically call the function with the parameters
    functionCell.values = [[`=Haskoning.${func.name}(${paramCellReferences.join(",")})`]];

    await context.sync();
  });
}

export async function setJsonAsTable(responseItem: any, address: string, inputRanges: string[]): Promise<any> {
  return await Excel.run(async (context: any) => {
    // Extract sheet name and cell address
    const sheetName = address.split("!")[0];
    const cellAddress = address.split("!")[1];
    const sheet = context.workbook.worksheets.getItem(sheetName);
    const currentCell = sheet.getRange(cellAddress);

    // Process the data into a table format
    const table = tableConverter(responseItem);

    // Get the first row and cell value
    const firstRow = table[0];
    const cellValue = firstRow[0];

    // Check for overlapping input and output ranges to prevent circular references
    const tableHeight = table.length;
    const tableWidth = table[0].length;
    const outputRange = currentCell.getResizedRange(tableHeight - 1, tableWidth - 1);
    await outputRange.load("address").context.sync();
    const outputRangeAddress = outputRange.address;

    if (await circularReferenceDetector.checkCircularReference(outputRangeAddress, inputRanges)) {
      throw new Error("Circular reference detected. This would create a dependency loop.");
    }
    circularReferenceDetector.addDependency(outputRangeAddress, inputRanges);

    // Get surrounding region to clear existing content
    const tableRange = currentCell.getSurroundingRegion();
    await tableRange.load(["address"]).context.sync();
    // Clear everything below currentCell
    tableRange.getOffsetRange(1, 0).clear();
    // Clear everything to the right of currentCell
    tableRange.getOffsetRange(0, 1).clear();

    console.log(`Clear Contents: ${tableRange.address} excluding ${address}`);

    // Write first row (headers) if table is wider than 1 column
    if (tableWidth !== 1) {
      const firstRowAllButFirst = firstRow.slice(1);
      const startCellFirstRow = currentCell.getOffsetRange(0, 1);
      const rangeFirstRow = startCellFirstRow.getResizedRange(0, firstRowAllButFirst.length - 1);
      rangeFirstRow.values = [firstRowAllButFirst];
    }

    // Write the rest of the table (data rows)
    const restOfTable = table.slice(1);
    if (restOfTable.length > 0) {
      const startCell = currentCell.getOffsetRange(1, 0);
      const fullRange = startCell.getResizedRange(restOfTable.length - 1, restOfTable[0].length - 1);
      fullRange.values = restOfTable;
    }

    await context.sync();
    return cellValue;
  });
}
